import {
  DeleteOutlined,
  DownloadOutlined,
  DragOutlined,
  EditOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS as DndCSS } from '@dnd-kit/utilities';
import { Button, List, Space, Tooltip, Typography } from 'antd';
import React, { useEffect, useState } from 'react';

import { useGetDownloadSasUrl } from '@/api';
import { usePermission } from '@/hooks/usePermission';
import { FileItemResponse } from '@/proxy';
import { humanFileSize, showMessage, toGermanDate } from '@/shared/utils';

const FILE_TYPE_GROUPS = {
  pdf: ['.pdf'],
  document: ['.xls', '.xlsx', '.csv'],
  image: ['.jpg', '.jpeg', '.png', '.gif'],
  video: ['.mp4', '.mov', '.avi'],
};

// Get file icon based on extension
const getFileIcon = (fileName: string) => {
  const extension = '.' + fileName.split('.').pop()?.toLowerCase();
  const iconStyle = { fontSize: '32px' };

  if (FILE_TYPE_GROUPS.pdf.includes(extension)) {
    return <FilePdfOutlined style={{ ...iconStyle, color: '#ff4d4f' }} />;
  }
  if (FILE_TYPE_GROUPS.document.includes(extension)) {
    if (extension === '.csv') {
      return <FileTextOutlined style={{ ...iconStyle, color: '#52c41a' }} />;
    }
    return <FileExcelOutlined style={{ ...iconStyle, color: '#52c41a' }} />;
  }
  if (FILE_TYPE_GROUPS.image.includes(extension)) {
    return <FileImageOutlined style={{ ...iconStyle, color: '#1890ff' }} />;
  }
  if (FILE_TYPE_GROUPS.video.includes(extension)) {
    return <PlayCircleOutlined style={{ ...iconStyle, color: '#722ed1' }} />;
  }
  return <FileOutlined style={iconStyle} />;
};

export type FileManagerProps = {
  files: FileItemResponse[];
  onOpenDeleteFileModal: (path: string) => void;
  onOpenRenameFileModal: (path: string, name: string) => void;
  onReorder?: (newFiles: FileItemResponse[]) => void;
};

interface SortableItemProps {
  item: FileItemResponse;
  onDelete: (e: React.MouseEvent<HTMLSpanElement>, path: string) => void;
  onDownload: (file: FileItemResponse) => void;
  onFilePreview: (file: FileItemResponse) => void;
  onRename: (e: React.MouseEvent<HTMLSpanElement>, path: string, name: string) => void;
  isMAAdmin: boolean;
}

function SortableItem({
  item,
  onDelete,
  onDownload,
  onFilePreview,
  onRename,
  isMAAdmin,
}: SortableItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.path,
  });

  const style = {
    transform: DndCSS.Transform.toString(transform),
    transition,
    opacity: 1,
    scale: isDragging ? 1.05 : 1,
    boxShadow: isDragging ? '0 5px 15px rgba(0,0,0,0.15)' : 'none',
    zIndex: isDragging ? 1 : 0,
    backgroundColor: isDragging ? '#fafafa' : 'transparent',
    borderRadius: isDragging ? '8px' : '0',
    padding: isDragging ? '8px' : '0',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <List.Item
        style={{ paddingLeft: 0, paddingRight: 0 }}
        actions={[
          <Typography.Text type='secondary'>{humanFileSize(item.size)}</Typography.Text>,
          <Tooltip title='Herunterladen'>
            <Button
              key='download'
              type='text'
              icon={<DownloadOutlined />}
              onClick={() => onDownload(item)}
            />
          </Tooltip>,
          isMAAdmin && (
            <Tooltip title='Umbenennen'>
              <Button
                key='rename'
                type='text'
                icon={<EditOutlined />}
                onClick={(e) => onRename(e, item.path, item.name)}
              />
            </Tooltip>
          ),
          isMAAdmin && (
            <Tooltip title='Löschen'>
              <Button
                key='delete'
                type='text'
                icon={<DeleteOutlined />}
                danger
                onClick={(e) => onDelete(e, item.path)}
              />
            </Tooltip>
          ),
          isMAAdmin && (
            <Tooltip title='Verschieben'>
              <span style={{ cursor: 'grab' }} {...attributes} {...listeners}>
                <DragOutlined style={{ fontSize: '16px', color: '#999' }} />
              </span>
            </Tooltip>
          ),
        ].filter(Boolean)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 40 }}>
          <span>{getFileIcon(item.name)}</span>
          <div
            style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 8 }}
            onClick={(e) => {
              e.stopPropagation();
              onFilePreview(item);
            }}
          >
            <Typography.Text style={{ cursor: 'pointer', fontWeight: 700, lineHeight: 1 }}>
              {item.name}
            </Typography.Text>
            <Typography.Text style={{ fontSize: 13, color: '#888', lineHeight: 1.2 }}>
              {toGermanDate(item.uploadedAt)}
            </Typography.Text>
          </div>
        </div>
      </List.Item>
    </div>
  );
}

export function FileManager({
  files,
  onOpenDeleteFileModal,
  onOpenRenameFileModal,
  onReorder,
}: FileManagerProps): React.JSX.Element {
  const { mutate: fetchSasUrl } = useGetDownloadSasUrl();
  const { isMAAdmin } = usePermission();
  const [currentFiles, setCurrentFiles] = useState<FileItemResponse[]>(files);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setCurrentFiles(files);
  }, [files]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const onDeleteFile = (e: React.MouseEvent<HTMLSpanElement>, path: string) => {
    e.stopPropagation();
    onOpenDeleteFileModal(path);
  };

  const onRenameFileClick = (e: React.MouseEvent<HTMLSpanElement>, path: string, name: string) => {
    e.stopPropagation();
    onOpenRenameFileModal(path, name);
  };

  const handleFilePreview = (file: FileItemResponse) => {
    fetchSasUrl(file.path, {
      onSuccess: async (response) => {
        window.open(response.url, '_blank');
      },
      onError: (error) => {
        showMessage('error', `Fehler beim Abrufen der Datei: ${error}`);
      },
    });
  };

  const handleDownload = (file: FileItemResponse) => {
    fetchSasUrl(file.path, {
      onSuccess: async (response) => {
        try {
          const blobResponse = await fetch(response.url);
          const blob = await blobResponse.blob();

          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = file.name;
          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
          showMessage('error', `Fehler beim Herunterladen der Datei: ${error}`);
        }
      },
      onError: (error) => {
        showMessage('error', `Fehler beim Abrufen der Datei: ${error}`);
      },
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = currentFiles.findIndex((file) => file.path === active.id);
      const newIndex = currentFiles.findIndex((file) => file.path === over.id);

      const newFiles = arrayMove(currentFiles, oldIndex, newIndex);
      setCurrentFiles(newFiles);
      setHasChanges(true);
    }
  };

  const handleSave = () => {
    onReorder?.(currentFiles);
    setHasChanges(false);
  };

  const handleCancel = () => {
    setCurrentFiles(files);
    setHasChanges(false);
  };

  return (
    <div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={currentFiles.map((file) => file.path)}
          strategy={verticalListSortingStrategy}
        >
          <List
            itemLayout='horizontal'
            dataSource={currentFiles}
            renderItem={(item) => (
              <SortableItem
                item={item}
                onDelete={onDeleteFile}
                onDownload={handleDownload}
                onFilePreview={handleFilePreview}
                onRename={onRenameFileClick}
                isMAAdmin={isMAAdmin}
              />
            )}
          />
        </SortableContext>
      </DndContext>
      {hasChanges && (
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Space>
            <Button onClick={handleCancel}>Verwerfen</Button>
            <Button type='primary' onClick={handleSave}>
              Speichern
            </Button>
          </Space>
        </div>
      )}
    </div>
  );
}
